import { useEffect, useState } from "react";
import { getApiUrl } from "../utils/api";
import { FaUsers } from "react-icons/fa";

export default function ShareToGroupModal({ open, poemId, poemTitle, onClose, onShared }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sharingId, setSharingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !poemId) return;

    const loadGroups = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          getApiUrl(`/api/groups/mine?poemId=${encodeURIComponent(poemId)}`),
          { credentials: "include" }
        );
        if (!res.ok) throw new Error("Failed to load groups");
        const data = await res.json();
        setGroups(Array.isArray(data) ? data : []);
      } catch {
        setError("Could not load your groups.");
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };

    loadGroups();
  }, [open, poemId]);

  const handleShare = async (groupId) => {
    setSharingId(groupId);
    setError("");
    try {
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/share`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ poemId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to share");
      setGroups((prev) =>
        prev.map((g) =>
          String(g._id) === String(groupId) ? { ...g, hasSharedPoem: true } : g
        )
      );
      window.dispatchEvent(
        new CustomEvent("groups-updated", {
          detail: { groupId, delta: 1, skipFetch: true },
        })
      );
      onShared?.(groupId, data);
    } catch (err) {
      setError(err.message || "Failed to share poem.");
    } finally {
      setSharingId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#333333]/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !sharingId) onClose();
      }}
    >
      <div
        className="group-modal w-full max-w-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-group-title"
      >
        <div className="group-modal__accent" aria-hidden="true" />
        <div className="group-modal__body">
          <h2 id="share-group-title" className="group-modal__title">
            Share to group
          </h2>
          <p className="group-modal__subtitle">
            {poemTitle
              ? `Share "${poemTitle}" with a group you're in.`
              : "Share this poem with a group you're in."}
          </p>

          {loading ? (
            <p className="text-sm text-[#777777] py-4">Loading your groups...</p>
          ) : groups.length === 0 ? (
            <p className="text-sm text-[#777777] py-4">
              You&apos;re not in any groups yet. Join or create a group from the sidebar first.
            </p>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {groups.map((group) => (
                <li
                  key={group._id}
                  className="flex items-center justify-between gap-3 p-3 border border-[#eeeeee] rounded"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#333333] truncate">{group.name}</p>
                    <p className="text-xs text-[#777777] flex items-center gap-1">
                      <FaUsers className="w-3 h-3" />
                      {group.memberCount} member{group.memberCount === 1 ? "" : "s"}
                    </p>
                  </div>
                  {group.hasSharedPoem ? (
                    <span className="text-xs font-semibold text-[#f26522] border border-[#ffd4bc] bg-[#fff5ef] px-2 py-1 rounded shrink-0">
                      Shared
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleShare(group._id)}
                      disabled={!!sharingId}
                      className="peer-home-btn-primary text-sm py-1.5 px-3 disabled:opacity-50 shrink-0"
                    >
                      {sharingId === group._id ? "..." : "Share"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {error && <p className="text-sm text-red-700 mt-3">{error}</p>}

          <div className="flex justify-end mt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={!!sharingId}
              className="peer-home-btn-secondary disabled:opacity-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
